import { useCallback, useEffect, useRef, useState } from "react"
import { HStack, Spacer, Stack } from "@chakra-ui/react"
import { createPlaidLinkToken, submitPlaidPayment, getAccountChangeCheck, IdentityCheckResult, PaymentsApiError } from "@/api"
import { Button, Text } from "@/components"
import { ensurePaymentsClient, getApiErrorMessage } from "../utils"
import { AccountChangeConfirm, ACCOUNT_IN_PROCESS_TEXT, accountChangeSuccessText } from "./AccountChangeConfirm"

interface Props {
  defaultClientData: {
    firstName: string
    lastName: string
    email: string
    companyName: string
    esoId?: number | null
    phoneNumber?: string
  }
  dealEsoId?: number
  paymentAmount: number
  paymentLabel: string
  onSuccess?: (message: string) => void
  // Fired when Plaid Link exits with an error (institution issue, auth failure,
  // unsupported bank, etc.). Parent uses this to unlock the manual-bank
  // fallback for the client (e.g. Schwab accounts that don't play well with Plaid).
  onPlaidFailure?: (reason: string) => void
}

// Plaid error codes that strongly indicate "this bank isn't going to work via
// Plaid" — surface the manual-entry fallback when we hit any of them. We also
// treat any non-null exit error as a failure (broad, but matches user intent
// of "they couldn't connect").
const INSTITUTION_FAILURE_CODES = new Set([
  "INSTITUTION_NOT_RESPONDING",
  "INSTITUTION_NOT_AVAILABLE",
  "INSTITUTION_DOWN",
  "INSTITUTION_NO_LONGER_SUPPORTED",
  "INVALID_INSTITUTION",
  "ITEM_LOCKED",
  "ITEM_NO_ERROR",
  "NO_ACCOUNTS",
  "PRODUCT_NOT_READY",
  "PRODUCTS_NOT_SUPPORTED",
  "INSUFFICIENT_CREDENTIALS",
])

export const UsPlaidWireInformationComponent = ({ defaultClientData, dealEsoId, paymentAmount, paymentLabel, onSuccess, onPlaidFailure }: Props) => {
  const [isInitializing, setIsInitializing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [linkToken, setLinkToken] = useState("")
  const [message, setMessage] = useState("")
  const [messageType, setMessageType] = useState<"info" | "error" | "success">("info")
  const [isPlaidLoaded, setIsPlaidLoaded] = useState(false)
  const [identityCheck, setIdentityCheck] = useState<IdentityCheckResult | null>(null)
  const clientIdRef = useRef<string>("")
  const plaidHandlerRef = useRef<PlaidHandler | null>(null)
  // A payment waiting for approval that this connect would cancel and send again for
  // approval to the new account (Bill, 2026-10-13). Asked BEFORE Plaid opens: the server
  // refuses an unconfirmed change only after Plaid's one-time token is spent, and the
  // client would have to connect all over again.
  const [pendingChange, setPendingChange] = useState<{ amount: number; currency?: string } | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const confirmedChangeRef = useRef(false)
  const hasInitialized = useRef(false)
  // A visit with nothing staged is a save-only visit: the client is registering an
  // account for later, not authorising a payment. Showing a bold $0.00 there reads
  // as an error — and it is the DOMINANT state on this form for anyone who just
  // disconnected, since the disconnect button only unlocks once outstanding and
  // pending are zero. ManualBankFormComponent has handled this since it shipped;
  // this form never did. (A client paid $38,359.98 would land here reading
  // "Payments Released from ESO Fund / $0.00".)
  const isSaveOnly = !(paymentAmount > 0)
  const paymentAmountRef = useRef(paymentAmount)
  paymentAmountRef.current = paymentAmount

  const showMessage = (msg: string, type: "info" | "error" | "success" = "info") => {
    setMessage(msg)
    setMessageType(type)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount)
  }

  const initializePlaid = useCallback(async () => {
    showMessage("")
    setIsInitializing(true)
    try {
      if (!defaultClientData.firstName || !defaultClientData.lastName || !defaultClientData.email || !defaultClientData.companyName) {
        throw new Error("Missing required client information (name, email, or company).")
      }

      const paymentsClient = await ensurePaymentsClient({
        first_name: defaultClientData.firstName,
        last_name: defaultClientData.lastName,
        email: defaultClientData.email,
        company_name: defaultClientData.companyName,
        client_number: String(defaultClientData.esoId || 0),
      })

      clientIdRef.current = paymentsClient.id

      const linkTokenResponse = await createPlaidLinkToken({
        client_id: paymentsClient.id,
        phone_number: defaultClientData.phoneNumber || undefined,
      })
      setLinkToken(String(linkTokenResponse.link_token || ""))
      showMessage("Bank connection ready. Click Connect Bank to proceed.", "info")
    } catch (error) {
      showMessage(getApiErrorMessage(error), "error")
    } finally {
      setIsInitializing(false)
    }
  }, [defaultClientData, paymentAmount])

  const completePlaidPayment = useCallback(async (publicToken: string, accountId: string) => {
    showMessage("")
    setIsSubmitting(true)
    try {
      const currentAmount = paymentAmountRef.current

      // Submit to Plaid endpoint — amount=0 means connect bank only
      const result = await submitPlaidPayment({
        client_id: clientIdRef.current,
        public_token: publicToken,
        account_id: accountId,
        amount: currentAmount > 0 ? currentAmount : 0,
        deal_eso_id: dealEsoId,
        confirm_cancel_pending: confirmedChangeRef.current ? true : undefined,
      })
      confirmedChangeRef.current = false

      // Capture identity-check result so we can render an inline banner.
      // A mismatch is non-blocking — the bank is still connected — but we
      // surface it to the client and email the equity partner.
      const identity = result.identity_check ?? null
      setIdentityCheck(identity)
      const hasMismatch = identity ? identity.match === false : false

      // Defer reload/onSuccess if there's a mismatch so the client can read
      // the banner. Otherwise behave as before.
      if (hasMismatch) {
        const baseMsg = accountChangeSuccessText("Your bank account has been connected.", result)
          ?? (currentAmount > 0 && !result.existing_transaction
            ? `Your bank account has been connected and payment of ${formatCurrency(currentAmount)} has been submitted for approval.`
            : "Your bank account has been connected.")
        showMessage(baseMsg + " Please review the identity notice below.", "info")
        return
      }

      // A replace that found a payment already awaiting approval re-points it to
      // the new account instead of creating a second one — say that, not
      // "submitted for approval", which reads like a new payment.
      // An account change that touched a payment says what happened to it (Bill,
      // 2026-10-13); a re-link of the same account, that its payment already waits.
      // repointed_transaction is the older server's re-point, for a website deployed
      // ahead of the payments server.
      const msg = accountChangeSuccessText("Your bank account has been securely connected.", result)
        ?? (result.existing_transaction
          ? "Your bank account has been securely connected. Your payment is already waiting for approval."
          : result.repointed_transaction
            ? `Your bank account has been securely connected. Your pending payment (${result.repointed_transaction}) will be deposited to this account and remains awaiting ESO Fund approval.`
            : currentAmount > 0
              ? `Your bank account has been securely connected and payment of ${formatCurrency(currentAmount)} has been submitted for approval. Approval emails have been sent automatically. Transaction ID: ${result.transaction?.id ?? "pending"}.`
              : "Your bank account has been securely connected. You will be able to accept pending liquidity once it is available.")

      if (onSuccess) {
        onSuccess(msg)
      } else {
        showMessage(msg, "success")
        setTimeout(() => window.location.reload(), 2000)
      }
    } catch (error) {
      confirmedChangeRef.current = false
      // A payment started waiting for approval after the check: the server asks for the
      // confirmation the check would have. Plaid's token is spent, so the client connects
      // again, and Connect Bank asks first this time.
      const askAgain = error instanceof PaymentsApiError && error.code === "CONFIRM_CANCEL_PENDING"
      showMessage(askAgain
        ? "A payment started waiting for approval while you were connecting, so your new account was not saved. Click Connect Bank to review it and connect again."
        : getApiErrorMessage(error), "error")
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  // Load Plaid SDK
  useEffect(() => {
    if (window.Plaid) {
      setIsPlaidLoaded(true)
      return
    }

    const existingScript = document.getElementById("plaid-link-sdk")
    if (existingScript) {
      existingScript.addEventListener("load", () => setIsPlaidLoaded(true))
      return
    }

    const script = document.createElement("script")
    script.id = "plaid-link-sdk"
    script.src = "https://cdn.plaid.com/link/v2/stable/link-initialize.js"
    script.async = true
    script.onload = () => setIsPlaidLoaded(true)
    script.onerror = () => showMessage("Failed to load Plaid Link SDK.", "error")
    document.body.appendChild(script)
  }, [])

  // Auto-initialize Plaid link token once we have required data
  useEffect(() => {
    if (
      !hasInitialized.current
      && defaultClientData.firstName
      && defaultClientData.email
    ) {
      hasInitialized.current = true
      initializePlaid()
    }
  }, [defaultClientData, initializePlaid])

  // Create Plaid handler when link token is ready
  useEffect(() => {
    if (!isPlaidLoaded || !linkToken || !window.Plaid) {
      return
    }

    if (plaidHandlerRef.current) {
      plaidHandlerRef.current.destroy()
      plaidHandlerRef.current = null
    }

    const handleSuccess = (publicToken: string, metadata: PlaidMetadata) => {
      const accountId =
        metadata?.account_id ||
        (metadata?.accounts && metadata.accounts[0]?.id) ||
        ""
      if (!accountId) {
        showMessage("No bank account was selected. Please try again.", "error")
        return
      }
      void completePlaidPayment(publicToken, accountId)
    }

    plaidHandlerRef.current = window.Plaid.create({
      token: linkToken,
      onSuccess: handleSuccess,
      onExit: (error: { error_code?: string; error_message?: string; display_message?: string } | null) => {
        // Closing Plaid ends this change: the next Connect Bank asks again.
        confirmedChangeRef.current = false
        if (!error) return // user just closed the modal — no signal to act on
        const code = error.error_code || ""
        const msg = error.display_message || error.error_message || "We couldn't connect to your bank through Plaid."
        showMessage(`${msg} You can also try connecting your bank manually.`, "error")
        // Any non-null exit error is treated as a Plaid failure for this client
        // (matches user intent: "if their bank doesn't support Plaid").
        // Specific institution codes get a more pointed reason for telemetry.
        const reason = INSTITUTION_FAILURE_CODES.has(code)
          ? `Plaid couldn't connect to your bank (${code}).`
          : msg
        if (onPlaidFailure) onPlaidFailure(reason)
      },
      onEvent: () => {},
    })


    return () => {
      if (plaidHandlerRef.current) {
        plaidHandlerRef.current.destroy()
        plaidHandlerRef.current = null
      }
    }
  }, [isPlaidLoaded, linkToken, completePlaidPayment])

  const openPlaidLink = async () => {
    if (!plaidHandlerRef.current) {
      showMessage("Plaid is not ready yet. Please wait a moment.", "error")
      return
    }
    showMessage("")
    // What a change would do to the client's payments in flight. null (no answer): open
    // Plaid as before; the payments server re-checks under lock and refuses what it must.
    if (clientIdRef.current && !confirmedChangeRef.current) {
      setIsChecking(true)
      const check = await getAccountChangeCheck(clientIdRef.current)
      setIsChecking(false)
      if (check?.in_process) {
        showMessage(ACCOUNT_IN_PROCESS_TEXT, "error")
        return
      }
      if (check?.waiting) {
        setPendingChange({ amount: check.waiting.amount, currency: check.waiting.currency })
        return
      }
    }
    plaidHandlerRef.current?.open()
  }

  const continueChange = () => {
    confirmedChangeRef.current = true
    setPendingChange(null)
    plaidHandlerRef.current?.open()
  }

  const messageColor = messageType === "error" ? "red.600" : messageType === "success" ? "green.600" : "gray.600"

  return (
    <Stack w="100%" bg="white" gap={5}>
      <Text fontSize="md">
        Connect your US bank account through Plaid to securely provide payment details.
      </Text>
      <Text fontSize="sm" color="gray.600">
        Your bank credentials are handled securely by Plaid and stored encrypted on our servers.
      </Text>

      <Stack p={5} borderWidth="1px" borderRadius="md" borderColor="gray.200" bg="gray.50">
        <Text fontWeight="bold">How it works</Text>
        {/* There is nothing to review on a save-only visit — the box below says so —
            so the step is dropped rather than reworded, and the rest renumber. */}
        {!isSaveOnly && <Text fontSize="sm">1. Review the payment details below.</Text>}
        <Text fontSize="sm">{isSaveOnly ? "1." : "2."} Click Connect Bank to open the Plaid window.</Text>
        <Text fontSize="sm">{isSaveOnly ? "2." : "3."} Select your US checking account (ideally the one connected to your equity portal, e.g. Carta).</Text>
        <Text fontSize="sm">
          {isSaveOnly
            ? "3. Your account is saved and we will use it when a payment is ready."
            : "4. Payment is submitted for approval automatically."}
        </Text>
        <Text fontSize="xs" color="gray.600">
          Plaid SDK: {isPlaidLoaded ? "Ready" : "Loading..."} | Session: {isInitializing ? "Initializing..." : linkToken ? "Ready" : "Pending"}
        </Text>
      </Stack>

      <Stack p={5} borderWidth="1px" borderRadius="lg" borderColor="blue.200" bg="blue.50">
        <Text fontSize="sm" color="gray.600">{paymentLabel}</Text>
        {isSaveOnly ? (
          <Text fontSize="md" fontWeight="semibold">
            No payment is due yet. Add your account now and we will use it when one is ready.
          </Text>
        ) : (
          <Text fontSize="2xl" fontWeight="bold">{formatCurrency(paymentAmount)}</Text>
        )}
      </Stack>

      {pendingChange ? (
        <AccountChangeConfirm waiting={pendingChange} onContinue={continueChange} onCancel={() => setPendingChange(null)} />
      ) : (
        <HStack>
          <Spacer />
          <Button
            variant="solid"
            w="fit-content"
            disabled={!isPlaidLoaded || !linkToken || isSubmitting || isChecking}
            onClick={() => { void openPlaidLink() }}
          >
            {isSubmitting ? "Connecting..." : "Connect Bank"}
          </Button>
        </HStack>
      )}
      {identityCheck && identityCheck.match === false ? (
        <Stack p={5} borderWidth="1px" borderRadius="lg" borderColor="orange.300" bg="orange.50">
          <Text fontSize="md" fontWeight="semibold" color="orange.800">
            Bank account owner doesn't fully match our records
          </Text>
          <Stack gap={1} mt={1}>
            {(["name", "email", "phone"] as const).map((field) => {
              const isMismatch = identityCheck.mismatches.includes(field)
              return (
                <Text key={field} fontSize="sm" color={isMismatch ? "red.700" : "green.700"}>
                  {isMismatch ? "\u2716" : "\u2714"}{" "}
                  <Text as="span" fontWeight="medium" textTransform="capitalize">{field}:</Text>{" "}
                  {isMismatch ? "doesn\u2019t match" : "matches"}
                </Text>
              )
            })}
          </Stack>
          <Text fontSize="sm" color="gray.700" mt={2}>
            We've notified your equity partner by email to confirm whether to proceed. Your bank
            connection has been saved and any pending payment is awaiting standard approval.
          </Text>
          <Text fontSize="sm" color="gray.700">
            If you know why these don't match (e.g. joint account with a spouse, secondary email on
            file with the bank, or an old phone number), please reply to your equity partner with a
            short explanation so they can confirm faster.
          </Text>
        </Stack>
      ) : null}
      {message ? <Text fontSize="sm" color={messageColor}>{message}</Text> : null}
    </Stack>
  )
}

type PlaidHandler = {
  open: () => void
  destroy: () => void
}

type PlaidAccount = {
  id: string
  name?: string
  mask?: string
  type?: string
  subtype?: string
}

type PlaidMetadata = {
  account_id?: string
  accounts?: PlaidAccount[]
  institution?: { name: string; institution_id: string }
}

type PlaidFactory = {
  create: (config: {
    token: string
    onSuccess: (publicToken: string, metadata: PlaidMetadata) => void
    onExit?: (error: { error_message?: string } | null) => void
    onEvent?: (eventName: string) => void
  }) => PlaidHandler
}

declare global {
  interface Window {
    Plaid?: PlaidFactory
  }
}
